import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/dist/query/react";
import {getAuthToken} from "../store/reducers/AuthSlice";
import {ICategory, ICategoryRecord, ICategoryUsage, ICategoryWrite, Tag} from "../model/ICategory";
import {TransactionType} from "../model/TransactionType";
import {ICurrency} from "../model/ICurrency";
import {Tags} from "./Tags";
import {IBulkUploadRow, IBulkUploadRowPatchRequest, IBulkUploadSession} from "../model/IBulkUpload";
import {getBaseUrl} from "./ApiUrls";
import {IBalance} from "../model/IAccount";
import {IFilterStats, IFilterStatsReq} from "../model/IStats";

export const categoryApi = createApi({
    reducerPath: 'categoryApi',
    tagTypes: [Tags.AccountLogos, Tags.AccountsTotalBalance, Tags.BulkUploadSession, Tags.BulkUploadSessionRows, Tags.Category],
    baseQuery: fetchBaseQuery({
        baseUrl: getBaseUrl() + '/api/v1/',
        prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
                headers.set('Authorization', token);
            }
            return headers;
        }
    }),
    endpoints: (build) => ({
        fetchAllCategories: build.query<ICategory[], TransactionType>({
            query: (trType) => ({
                url: 'categories/' + trType + '/',
            }),
            providesTags: (_result, _error, trType) => [{type: Tags.Category, id: trType}],
        }),
        createCategory: build.mutation<ICategoryRecord, {trType: TransactionType, name: string, root_category?: number | null}>({
            query: ({trType, ...body}) => ({
                url: 'categories/' + trType + '/',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, {trType}) => [{type: Tags.Category, id: trType}],
        }),
        updateCategory: build.mutation<ICategoryRecord, {trType: TransactionType, id: number} & ICategoryWrite>({
            query: ({trType, id, ...body}) => ({
                url: 'categories/' + trType + '/' + id + '/',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, {trType}) => [{type: Tags.Category, id: trType}],
        }),
        moveCategory: build.mutation<ICategoryRecord, {trType: TransactionType, id: number, parent_id: number | null, position: number}>({
            query: ({trType, id, parent_id, position}) => ({
                url: 'categories/' + trType + '/' + id + '/move/',
                method: 'POST',
                body: {parent_id, position},
            }),
            invalidatesTags: (_result, _error, {trType}) => [{type: Tags.Category, id: trType}],
        }),
        fetchCategoryUsage: build.query<ICategoryUsage, {trType: TransactionType, id: number}>({
            query: ({trType, id}) => ({
                url: 'categories/' + trType + '/' + id + '/usage/',
            }),
        }),
        deleteCategory: build.mutation<void, {trType: TransactionType, id: number, replacement?: number | null}>({
            query: ({trType, id, replacement}) => ({
                url: 'categories/' + trType + '/' + id + '/delete/',
                method: 'POST',
                body: {replacement: replacement ?? null},
            }),
            invalidatesTags: (_result, _error, {trType}) => [{type: Tags.Category, id: trType}],
        }),
        fetchAllTags: build.query<Tag[], void>({
            query: () => ({
                url: 'categories/tag/',
            }),
        }),
        fetchAllCurrencies: build.query<ICurrency[], void>({
            query: () => ({
                url: 'currencies/currency/',
            }),
        }),
        fetchAllAccountLogos: build.query<string[], void>({
            query: () => ({
                url: 'accounts/logos/',
            }),
            providesTags: [Tags.AccountLogos],
        }),
        fetchAccountsTotalBalance: build.query<IBalance[], void>({
            query: () => ({
                url: 'accounts/total_balance/',
            }),
            providesTags: [Tags.AccountsTotalBalance],
        }),
        fetchAllBulkUploadSessions: build.query<IBulkUploadSession[], void>({
            query: () => ({
                url: 'bulk_upload/session/',
            }),
            providesTags: [Tags.BulkUploadSession],
        }),
        getBulkUploadSession: build.query<IBulkUploadSession, string>({
            query: (session_id) => ({
                url: 'bulk_upload/session/' + session_id + '/',
            }),
        }),
        createBulkUploadSession: build.mutation<IBulkUploadSession, {account_id: number, file: File, comment?: string}>({
            query: ({account_id, file, comment}) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('account', account_id.toString());
                if (comment) {
                    formData.append('comment', comment);
                }
                return {
                    url: 'bulk_upload/upload/',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: [Tags.BulkUploadSession],
        }),
        deleteBulkUploadSession: build.mutation<void, number>({
            query: (id) => ({
                url: 'bulk_upload/session/' + id + '/',
                method: 'DELETE',
            }),
            invalidatesTags: [Tags.BulkUploadSession, Tags.BulkUploadSessionRows],
        }),
        fetchAllBulkUploadRows: build.query<IBulkUploadRow[], string>({
            query: (session_id) => ({
                url: 'bulk_upload/rows/?session_id=' + session_id,
            }),
            providesTags: [Tags.BulkUploadSessionRows],
        }),
        patchBulkUploadRow: build.mutation<IBulkUploadRow, IBulkUploadRowPatchRequest>({
            query: ({id, ...body}) => ({
                url: 'bulk_upload/rows/' + id + '/',
                method: 'PATCH',
                body: body,
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows],
        }),
        patchBulkUploadRows: build.mutation<IBulkUploadRow[], IBulkUploadRowPatchRequest[]>({
            query: (body) => ({
                url: 'bulk_upload/rows/',
                method: 'PATCH',
                body: body,
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows],
        }),
        splitBulkUploadRow: build.mutation<{parent: IBulkUploadRow, children: IBulkUploadRow[]}, number>({
            query: (id) => ({
                url: 'bulk_upload/rows/' + id + '/split/',
                method: 'POST',
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows, Tags.BulkUploadSession],
        }),
        addBulkUploadChildRow: build.mutation<IBulkUploadRow, number>({
            query: (id) => ({
                url: 'bulk_upload/rows/' + id + '/add_child/',
                method: 'POST',
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows, Tags.BulkUploadSession],
        }),
        deleteBulkUploadChildRow: build.mutation<{unsplit: boolean, parent_id: number, deleted_id?: number}, number>({
            query: (id) => ({
                url: 'bulk_upload/rows/' + id + '/',
                method: 'DELETE',
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows, Tags.BulkUploadSession],
        }),
        uniteBulkUploadRows: build.mutation<{parent: IBulkUploadRow, children: IBulkUploadRow[]}, number[]>({
            query: (row_ids) => ({
                url: 'bulk_upload/rows/unite/',
                method: 'POST',
                body: {row_ids},
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows, Tags.BulkUploadSession],
        }),
        cancelUniteBulkUploadRow: build.mutation<{cancelled: boolean, parent_id: number, restored_ids: number[]}, number>({
            query: (id) => ({
                url: 'bulk_upload/rows/' + id + '/cancel_unite/',
                method: 'POST',
            }),
            invalidatesTags: [Tags.BulkUploadSessionRows, Tags.BulkUploadSession],
        }),
        reconcileBulkUploadSession: build.mutation<void, string>({
            query: (id) => ({
                url: 'bulk_upload/session/' + id + '/reconcile/',
                method: 'POST',
            }),
            invalidatesTags: [Tags.BulkUploadSession, Tags.BulkUploadSessionRows],
        }),
        fetchStatsByFilter: build.query<IFilterStats, IFilterStatsReq>({
            query: (request) => ({
                url: 'transactions/stats/filter/',
                params: request,
            }),
        }),
    }),
});